﻿import { GetFutureTimeOptions, ServerTimeData, TimeApi } from './TimeApi'
import { createMockDelay, MOCK_DELAYS } from '../rundot-game-api/systems/core'
import { RundotGameAPI } from '../rundot-game-api/types'
import { isPacificDaylightTime } from './utils'

export class MockTimeApi implements TimeApi {
  private readonly rundotGameApi: RundotGameAPI

  constructor(rundotGameApi: RundotGameAPI) {
    this.rundotGameApi = rundotGameApi
  }

  formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
    const locale = this.rundotGameApi.getLocale()

    // Format options
    const numberOptions = {
      style: options?.style || 'decimal',
      minimumFractionDigits: options?.minimumFractionDigits || 0,
      maximumFractionDigits: options?.maximumFractionDigits || 2,
      ...options,
    }

    return value.toLocaleString(locale, numberOptions)
  }

  formatTime(timestamp: number, options?: any): string {
    const locale = this.rundotGameApi.getLocale()

    // Create a Date object from the timestamp
    const date = new Date(timestamp)

    // Format options
    const dateTimeOptions = {
      dateStyle: options.dateStyle || 'medium',
      timeStyle: options.timeStyle || 'medium',
      hour12: options.hour12 !== undefined ? options.hour12 : true,
      ...options,
    }

    return date.toLocaleString(locale, dateTimeOptions)
  }

  async getFutureTimeAsync(options?: GetFutureTimeOptions): Promise<number> {
    // Get the current server time first - now returns direct TimeInfo object
    const timeInfo = await this.requestTimeAsync()

    // Start with the server time
    const serverTime = new Date(timeInfo.serverTime)
    const result = new Date(serverTime)

    // Add days, hours, and minutes if specified
    if (options?.days) {
      result.setDate(result.getDate() + options.days)
    }

    if (options?.hours) {
      result.setHours(result.getHours() + options.hours)
    }

    if (options?.minutes) {
      result.setMinutes(result.getMinutes() + options.minutes)
    }

    // Set specific time of day if requested
    if (options?.timeOfDay) {
      const { hour = 0, minute = 0, second = 0 } = options.timeOfDay

      // If we're setting the time of day, we do it in the user's local timezone
      // Get timezone offset in minutes
      const timezoneOffset =
        timeInfo.timezoneOffset || new Date().getTimezoneOffset()

      // First set the UTC time that corresponds to the local time
      const localHour = hour

      // Reset hours, minutes, seconds
      result.setHours(localHour, minute, second, 0)
    }

    // For specific timezones, we need to convert
    if (options?.timezone) {
      switch (options?.timezone.toUpperCase()) {
        case 'PT':
        case 'PST':
        case 'PDT': {
          // Handle Pacific Time conversion
          // First determine if date is in PDT
          const isPDT = isPacificDaylightTime(result)
          const ptOffset = isPDT ? -7 : -8 // PDT is UTC-7, PST is UTC-8

          // Reset time to midnight UTC
          const utcDay = result.getUTCDate()
          const utcMonth = result.getUTCMonth()
          const utcYear = result.getUTCFullYear()
          result.setUTCHours(0, 0, 0, 0)

          // Add the specific time in PT (converting to UTC)
          const { hour = 0, minute = 0, second = 0 } = options.timeOfDay || {}

          // Convert PT time to UTC time
          // e.g., 9am PT = 16:00 or 17:00 UTC
          const utcHour = (hour - ptOffset) % 24
          result.setUTCHours(utcHour, minute, second, 0)
          break
        }
        // Add other timezone cases as needed
        default:
          console.warn(
            `[RUN:mock] Timezone ${options.timezone} not supported, using local time`,
          )
      }
    }

    // Return direct timestamp number to match real API
    return result.getTime()
  }

  async requestTimeAsync(): Promise<ServerTimeData> {
    await createMockDelay(MOCK_DELAYS.short)

    const rundotGameApi = this.rundotGameApi

    // In the mock, we'll simulate server-client time offset
    // This helps developers test time sync behavior

    // Simulate a small offset between server and client time
    // In production, this could be seconds or even minutes
    const mockOffset = rundotGameApi._mock.serverTimeOffset || 2500 // Default 2.5 second offset
    const mockServerTime = Date.now() + mockOffset

    // Get timezone offset in minutes (positive for west of UTC, negative for east)
    const timezoneOffset = new Date().getTimezoneOffset()

    // Convert server time (UTC) to local time
    // Note: getTimezoneOffset() returns minutes WEST of UTC with reversed sign convention:
    // - For UTC-5 (e.g., Eastern Time): Returns +300 minutes (positive)
    // - For UTC+3 (e.g., Moscow): Returns -180 minutes (negative)
    // So we subtract the offset (which adds time for eastern timezones and subtracts for western)
    const localTime = mockServerTime - timezoneOffset * 60000

    // Return direct TimeInfo object (not wrapped in success object)
    const timeInfo = {
      serverTime: mockServerTime,
      localTime,
      timezoneOffset: timezoneOffset,
      formattedTime: new Date(localTime).toISOString(),
      locale: rundotGameApi._mock.user?.locale || 'en-US',
    }

    return timeInfo
  }
}
